import { FileSizeValidator, FileSizeValidatorKey, FileTypeValidator, FileTypeValidatorKey, RequiredValidatorKey } from '@spa/validators';
import { FileExtensionsEnum, FileMimeTypesEnum } from '../../enums';
import { Language } from '../../language';
import { MessageFunctionType } from '../../types';
import {Observable, Subscriber} from "rxjs";

export class FilesHelper {
  protected constructor() {
  }

  static validateFile(
    file: File | null,
    messageFunction: MessageFunctionType,
    currentLanguage: string,
    requiredValidation = true
  ): string[] {
    const errors = [];
    const placeholder = currentLanguage === Language.English ? 'File' : 'Файл';

    if (file) {
      if (FileTypeValidator(false, file.type)(null)) {
        const typeErrorMessage = messageFunction(currentLanguage, placeholder)[FileTypeValidatorKey];

        errors.push(typeErrorMessage);
      }

      if (FileSizeValidator(false, file.size)(null)) {
        const sizeErrorMessage = messageFunction(currentLanguage, placeholder)[FileSizeValidatorKey];

        errors.push(sizeErrorMessage);
      }
    }
    else if (requiredValidation) {
      const requiredErrorMessage = messageFunction(currentLanguage, placeholder)[RequiredValidatorKey];

      errors.push(requiredErrorMessage);
    }

    return errors;
  }

  static getFileMimeType(fileName: string): string {
    const allowedFileExtensions = [
      FileExtensionsEnum.Pdf,
      FileExtensionsEnum.Jpg,
      FileExtensionsEnum.Jpeg,
      FileExtensionsEnum.Png
    ].join('|');

    const fileExtensionRegex = new RegExp('(?!\\.)(' + allowedFileExtensions + ')$', 'i');
    const fileExtensionMatches = fileName.match(fileExtensionRegex);

    if (!fileExtensionMatches) {
      return FileMimeTypesEnum.PlainText;
    }

    switch (fileExtensionMatches[0].toLowerCase()) {
      case FileExtensionsEnum.Pdf:
        return FileMimeTypesEnum.Pdf;
      case FileExtensionsEnum.Jpg:
      case FileExtensionsEnum.Jpeg:
        return FileMimeTypesEnum.Jpg;
      case FileExtensionsEnum.Png:
        return FileMimeTypesEnum.Png;
      default:
        return FileMimeTypesEnum.PlainText;
    }
  }

  static getImageFileMimeTypes(): string[] {
    return [
      FileMimeTypesEnum.Jpg,
      FileMimeTypesEnum.Jpeg,
      FileMimeTypesEnum.Png
    ];
  }

  static triggerDownloadFile(fileName: string, blob: Blob): void {
    // @ts-ignore
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // @ts-ignore
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    }
    else {
      const link = document.createElement('a');

      document.body.appendChild(link);

      link.style.display = 'none';

      const url = window.URL.createObjectURL(blob);

      link.href = url;
      link.download = fileName;

      link.click();

      window.URL.revokeObjectURL(url);

      document.body.removeChild(link);
    }
  }

  static convertBase64ToBlob(data: string, contentType = '', sliceSize = 512): Blob {
    const byteCharacters = atob(data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  static convertBlobToFile(blobFile: Blob, fileName: string): File {
    const blob: any = blobFile;

    blob.lastModifiedDate = new Date();
    blob.name = fileName;

    return blob as File;
  }

	static convertFileToRawBase64(file: File): Observable<string> {
		return new Observable((observer: Subscriber<string>) => {
			const reader = new FileReader();

			reader.readAsBinaryString(file);

			reader.onload = () => {
				observer.next(window.btoa(reader.result as string));
				observer.complete();
			};
			reader.onerror = () => {
				observer.error();
				observer.complete();
			};
		});
	}

  static createBase64DataUrl(data: string, mimeType: string): string {
    return 'data:' + mimeType + ';base64,' + data;
  }

  static getDocumentLibrary(language: string): { name: string; id: string; }[] {
    if (language === Language.Bulgarian) {
      return [
        {
          name: 'Искане за професионален клиент',
          id: 'Prof_Client_BG_v1.pdf'
        },
        {
          name: 'Декларация за произход на средствата',
          id: 'SOF_Individuals_BG_v1.pdf'
        },
        {
          name: 'ДЕКЛАРАЦИЯ по чл. 42, ал. 2, т. 2 от ЗМИП',
          id: 'PEPs_individuals_BG_v1.pdf'
        },
        {
          name: 'Заявка и поръчка за валутен обмен',
          id: 'CurrEF_BG_v1.pdf'
        },
      ];
    }

    return [
      {
        name: 'Professional client request',
        id: 'Prof_Client_EN_v1.pdf'
      },
      {
        name: 'Source of funds declaration',
        id: 'SOF_Individuals_EN_v1.pdf'
      },
      {
        name: 'PEPs declaration',
        id: 'PEPs_individuals_EN_v1.pdf'
      },
      {
        name: 'FX form',
        id: 'CurrEF_EN_v1.pdf'
      },
    ];
  }
}
